import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { useAuth } from '../hooks/useAuth';

interface LoginForm {
  email: string;
  password: string;
}

export function Login() {
  const [error, setError] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [credentials, setCredentials] = useState<LoginForm | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      setError('');
      const result = await login(data.email, data.password);
      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setCredentials(data);
      } else {
        navigate('/setup-2fa');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  if (requiresTwoFactor && credentials) {
    return <TwoFactorVerification email={credentials.email} password={credentials.password} />;
  }

  return (
    <AuthShell>
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground">Sign in to your account to continue</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="email">Email</label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
            })}
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="password">Password</label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            {...register('password', { required: 'Password is required' })}
          />
          {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          <Link
            to="/forgot-password"
            className="inline-block text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-40 mt-2"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  );
}

function TwoFactorVerification({ email, password }: { email: string; password: string }) {
  const [error, setError] = useState('');
  const { verifyTwoFactor } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<{ token: string }>();

  const onSubmit = async (data: { token: string }) => {
    try {
      setError('');
      await verifyTwoFactor(email, password, data.token);
      navigate('/payments');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    }
  };

  return (
    <AuthShell>
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Two-step verification</h1>
        <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="token">Authentication code</label>
          <Input
            id="token"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            className="tracking-[0.5em] text-center font-mono text-lg"
            {...register('token', {
              required: 'Code is required',
              pattern: { value: /^\d{6}$/, message: 'Must be 6 digits' }
            })}
          />
          {errors.token && <p className="text-xs text-destructive">{errors.token.message}</p>}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-40 mt-2"
        >
          {isSubmitting ? 'Verifying…' : 'Verify'}
        </button>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-foreground text-background flex-col justify-between p-12">
        <div className="text-lg font-semibold tracking-tight">AnotherSnusFactory</div>
        <div className="space-y-3">
          <p className="text-3xl font-semibold leading-snug">
            Payments &<br />reconciliation,<br />simplified.
          </p>
          <p className="text-sm text-background/50">
            Connect Fortnox with Citi Bank in one place.
          </p>
        </div>
        <p className="text-xs text-background/30">© {new Date().getFullYear()} AnotherSnusFactory</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-base font-semibold tracking-tight mb-10 text-foreground">
            AnotherSnusFactory
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
